import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Typography, Button, Row, Col, Card, Input, Space, Tag, Divider, Tooltip, Pagination, message, Spin, Tabs, Select, Collapse } from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import { SearchOutlined, ArrowRightOutlined, BankOutlined, CarOutlined, HomeOutlined, RocketOutlined, ShopOutlined, SafetyOutlined, FilterOutlined, DownOutlined, UpOutlined, ArrowUpOutlined, PlusOutlined, StarOutlined, LineChartOutlined, ThunderboltOutlined, DollarOutlined, CrownOutlined, ApartmentOutlined, RightOutlined } from '@ant-design/icons';
import CompanyLogoSVG from '../../assets/svg/company-logo.svg';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import ISINFilterSidebar from './ISINFilterSidebar';
import ISINCard from '../shared/ISINCard';
import { WatchlistPanel } from './Watchlist';
import './Instruments.css';
import './Watchlist.css';
import './LandingHero.css';
import apiClient from '../../services/apiClient';
import { mapCategoryForFilterApi, isRbiGovCategory } from '../../utils/categoryApiUtils';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const ISIN_SEARCH_DELAY_MS = 500;
const MIN_ISIN_SEARCH_QUERY_LENGTH = 3;

const Instruments = ({ skipLayout = false }) => {
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ref to track if component has mounted (to avoid conflicts between useEffects)
  const isInitialMount = useRef(true);
  // Ref for categories section — close dropdown when clicking outside
  const categoriesSectionRef = useRef(null);
  const isinSearchTimerRef = useRef(null);
  
  // Initialize state from URL params or defaults
  const [isinSearchQuery, setIsinSearchQuery] = useState(searchParams.get('search') || '');
  const [isinsData, setIsinsData] = useState([]);
  const [filteredIsins, setFilteredIsins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size')) || 12);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'ALL');
  const [categoryCounts, setCategoryCounts] = useState({});
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [filterSidebarVisible, setFilterSidebarVisible] = useState(false);
  const [currentFilters, setCurrentFilters] = useState({});
  const [filterLoading, setFilterLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [highlightedMainCategory, setHighlightedMainCategory] = useState(null);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoriesSectionRef.current && !categoriesSectionRef.current.contains(e.target)) {
        setExpandedCategories({});
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter states - initialize from URL params
  const [redemptionFilter, setRedemptionFilter] = useState(searchParams.get('redemption') || null);
  const [couponRateFilter, setCouponRateFilter] = useState(searchParams.get('couponRate') || null);
  const [ratingsFilter, setRatingsFilter] = useState(searchParams.get('ratings') || null);

  const isRbiGov = selectedCategory !== 'ALL' && isRbiGovCategory(selectedCategory);
  const showRatingFilter = !isRbiGov;
  const [filtersCollapsed, setFiltersCollapsed] = useState(searchParams.get('filtersOpen') === 'true' ? false : 'collapsed');
  const activeView = searchParams.get('view') === 'watchlist' ? 'watchlist' : 'browse';

  const setActiveView = useCallback((view) => {
    const next = new URLSearchParams(searchParams);
    if (view === 'watchlist') {
      next.set('view', 'watchlist');
    } else {
      next.delete('view');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Helper function to count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (redemptionFilter) count++;
    if (couponRateFilter) count++;
    if (showRatingFilter && ratingsFilter) count++;
    if (isinSearchQuery.trim()) count++;
    return count;
  };

  // Category definitions with API mappings
  const categories = {
    ALL: "All Categories",
    // Sovereign Securities
    slr: "SLR Bonds",
    nonSlr: "Non-SLR Bonds", 
    masalaBonds: "Masala Bonds",
    greenBonds: "Green Bonds",
    sdl: "SDL Bonds",
    // Corporate Securities
    highQualityLiquidBonds: "High Quality Liquid",
    investmentGradeBonds: "Investment Grade",
    regularIncomeBonds: "Regular Income",
    highYieldBonds: "High Yield",
    structuredBonds: "Structured Bonds",
    hybrids: "Hybrids",
    subInvestmentGradeBonds: "Sub-Investment Grade",
    REIT: "REIT",
    INVIT: "INVIT"
  };

  // Hierarchical category structure
  const categoryHierarchy = [
    { 
      key: 'ALL', 
      label: 'All Instruments', 
      icon: <PlusOutlined />,
      type: 'main'
    },
    {
      key: 'sovereign',
      label: 'Sovereign Securities',
      icon: <CrownOutlined />,
      type: 'main',
      subcategories: [
        { key: 'slr', label: 'SLR Bonds', icon: <CrownOutlined /> },
        { key: 'nonSlr', label: 'Non-SLR Bonds', icon: <SafetyOutlined /> },
        { key: 'masalaBonds', label: 'Masala Bonds', icon: <DollarOutlined /> },
        { key: 'greenBonds', label: 'Green Bonds', icon: <BankOutlined /> },
        { key: 'sdl', label: 'SDL Bonds', icon: <HomeOutlined /> }
      ]
    },
    {
      key: 'subSovereign',
      label: 'Sub-Sovereign Securities',
      icon: <CrownOutlined />,
      type: 'main',
      subcategories: [
        { key: 'centralGovernmentGuaranteedBonds', label: 'Central Government Guaranteed Bonds', icon: <CrownOutlined /> },
        { key: 'stateGovernmentGuaranteedBonds', label: 'State Government Guaranteed Bonds', icon: <SafetyOutlined /> },
        { key: 'municipalBonds', label: 'Municipal Bonds', icon: <DollarOutlined /> },
      ]
    },
    {
      key: 'corporate',
      label: 'Corporate Securities',
      icon: <ApartmentOutlined />,
      type: 'main',
      subcategories: [
        { key: 'highQualityLiquidBonds', label: 'High Quality Liquid', icon: <StarOutlined /> },
        { key: 'investmentGradeBonds', label: 'Investment Grade', icon: <BankOutlined /> },
        { key: 'regularIncomeBonds', label: 'Regular Income', icon: <BankOutlined /> },
        { key: 'highYieldBonds', label: 'High Yield', icon: <LineChartOutlined /> },
        { key: 'structuredBonds', label: 'Structured Bonds', icon: <ApartmentOutlined /> },
        { key: 'hybrids', label: 'Hybrids', icon: <RocketOutlined /> },
        { key: 'subInvestmentGradeBonds', label: 'Sub-Investment Grade', icon: <ThunderboltOutlined /> }

      ]
    },
    { 
      key: 'REIT', 
      label: 'REIT', 
      icon: <PlusOutlined />,
      type: 'main'
    },
    { 
      key: 'INVIT', 
      label: 'INVIT', 
      icon: <PlusOutlined />,
      type: 'main'
    },
     { 
      key: 'Masala Bonds  ', 
      label: 'Masala Bonds', 
      icon: <PlusOutlined />,
      type: 'main'
    },
    {
      key: 'Other',
      label: 'Other Securities',
      icon: <ApartmentOutlined />,
      type: 'main',
      subcategories: [
        {key:"Commercial Papers", label:"Commercial Papers", icon: <SafetyOutlined />},
        {key:"Certificate of Deposit", label:"Certificate of Deposit", icon: <SafetyOutlined />},
        {key:"Deposits", label:"Deposit", icon: <SafetyOutlined/>, 
          subcategories: [
            {key:"Bank Deposit", label:"Bank Deposit", icon: <SafetyOutlined />},
            {key:"Public Deposit", label:"Public Deposit", icon: <SafetyOutlined />},
            {key:"Postal Deposit", label:"Postal Deposit", icon: <SafetyOutlined />}
          ]
          },
        {key:"ABS", label:"ABS", icon: <StarOutlined />},
        {key:"MBS", label:"MBS", icon: <BankOutlined />},
        {key:"CMBS", label:"CMBS", icon: <LineChartOutlined />},
        {key:"Preference Shares", label:"Preference Shares", icon: <SafetyOutlined />},
        {key:"Hybrids", label:"Hybrids", icon: <SafetyOutlined />},
        {key:"Fixed Maturity Plans", label:"Fixed Maturity Plans", icon: <SafetyOutlined />},
        {key:"AIF Category II", label:"AIF Category II", icon: <SafetyOutlined />},
       
        ]
    },
   
  ];

  // Flatten category buttons for backward compatibility
  const categoryButtons = [
    { key: 'ALL', label: 'All Categories', icon: <PlusOutlined /> },
    // Sovereign Securities
    { key: 'slr', label: 'SLR Bonds', icon: <CrownOutlined /> },
    { key: 'nonSlr', label: 'Non-SLR Bonds', icon: <SafetyOutlined /> },
    { key: 'masalaBonds', label: 'Masala Bonds', icon: <DollarOutlined /> },
    { key: 'greenBonds', label: 'Green Bonds', icon: <BankOutlined /> },
    { key: 'sdl', label: 'SDL Bonds', icon: <HomeOutlined /> },
    // Corporate Securities
    { key: 'highQualityLiquidBonds', label: 'High Quality Liquid', icon: <StarOutlined /> },
    { key: 'investmentGradeBonds', label: 'Investment Grade', icon: <BankOutlined /> },
    { key: 'regularIncomeBonds', label: 'Regular Income', icon: <BankOutlined /> },
    { key: 'highYieldBonds', label: 'High Yield', icon: <LineChartOutlined /> },
    { key: 'structuredBonds', label: 'Structured Bonds', icon: <ApartmentOutlined /> },
    { key: 'hybrids', label: 'Hybrids', icon: <RocketOutlined /> },
    { key: 'subInvestmentGradeBonds', label: 'Sub-Investment Grade', icon: <ThunderboltOutlined /> }

  ];

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8083/api/v1/sb';

  // Advanced filter options
  const redemptionOptions = [
    { label: 'Select Redemption', value: null },
    { label: '1 Year', value: '1' },
    { label: '2 Years', value: '2' },
    { label: '3 Years', value: '3' },
    { label: '5 Years', value: '5' },
    { label: '7 Years', value: '7' },
    { label: '10+ Years', value: '10+' }
  ];

  const couponRateOptions = [
    { label: 'Select Coupon Rate', value: null },
    { label: '6%+', value: '6' },
    { label: '7%+', value: '7' },
    { label: '8%+', value: '8' },
    { label: '9%+', value: '9' },
    { label: '10%+', value: '10' },
    { label: '11%+', value: '11' },
    { label: '12%+', value: '12' }
  ];

  const ratingsOptions = [
    { label: 'Select Rating', value: null },
    { label: 'AAA', value: 'AAA' },
    { label: 'AAA(SO)', value: 'AAA(SO)' },
    { label: 'AA+', value: 'AA+' },
    { label: 'AA+(SO)', value: 'AA+(SO)' },
    { label: 'AA', value: 'AA' },
    { label: 'AA(SO)', value: 'AA(SO)' },
    { label: 'AA-', value: 'AA-' },
    { label: 'AA-(SO)', value: 'AA-(SO)' },
    { label: 'A+', value: 'A+' },
    { label: 'A+(SO)', value: 'A+(SO)' },
    { label: 'A', value: 'A' },
    { label: 'A(SO)', value: 'A(SO)' },
    { label: 'A-', value: 'A-' },
    { label: 'A-(SO)', value: 'A-(SO)' },
    { label: 'BBB+', value: 'BBB+' },
    { label: 'BBB+(SO)', value: 'BBB+(SO)' },
    { label: 'BBB', value: 'BBB' },
    { label: 'BBB(SO)', value: 'BBB(SO)' },
    { label: 'BBB-', value: 'BBB-' },
    { label: 'BBB-(SO)', value: 'BBB-(SO)' },
    { label: 'Below BBB-', value: 'Below BBB-' }
  ];

  // Update URL params helper
  const updateURLParams = (params) => {
    const newSearchParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Fetch category statistics
  const fetchCategoryStatistics = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/isin-category/statistics`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      if (result.statusCode === 200 && result.data) {
        const d = result.data;
        // Sub-Sovereign subcategory counts (from API or 0)
        const centralGovtGuaranteed = d.centralGovernmentGuaranteedBonds ?? d.centralGovernmentGuaranteed ?? 0;
        const stateGovtGuaranteed = d.stateGovernmentGuaranteedBonds ?? d.stateGovernmentGuaranteed ?? 0;
        const municipal = d.municipalBonds ?? d.municipal ?? 0;
        const subSovereignTotal = centralGovtGuaranteed + stateGovtGuaranteed + municipal;
        // Map API response keys to frontend category keys
        const mappedCounts = {
          // ALL / total
          ALL: d.totalIsins ?? 0,
          totalIsins: d.totalIsins || 0,
          // Sovereign Securities
          slr: d.government || 0,
          nonSlr: d.nonSlr ?? 0,
          masalaBonds: d.masalaBonds ?? 0,
          greenBonds: d.greenBonds ?? 0,
          sdl: d.stategovernment || 0,
          sovereign: (d.government || 0) + (d.stategovernment || 0) + (d.nonSlr ?? 0) + (d.masalaBonds ?? 0) + (d.greenBonds ?? 0),
          // Sub-Sovereign Securities
          subSovereign: d.subSovereign ?? subSovereignTotal,
          centralGovernmentGuaranteedBonds: centralGovtGuaranteed,
          stateGovernmentGuaranteedBonds: stateGovtGuaranteed,
          municipalBonds: municipal,
          // Corporate Securities
          highQualityLiquidBonds: d.highQualityLiquidBonds || 0,
          investmentGradeBonds: d.investmentGradeBonds || 0,
          regularIncomeBonds: d.regularIncomeBonds || 0,
          highYieldBonds: d.highYieldBonds || 0,
          subInvestmentGradeBonds: d.subInvestmentGradeBonds || 0,
          structuredBonds: d.structuredBonds || 0,
          hybrids: d.hybrids || 0,
          corporate: (d.highQualityLiquidBonds || 0) + (d.investmentGradeBonds || 0) + (d.regularIncomeBonds || 0) + (d.highYieldBonds || 0) + (d.subInvestmentGradeBonds || 0) + (d.structuredBonds || 0) + (d.hybrids || 0),
          // Other main categories
          REIT: d.REIT ?? d.reit ?? 0,
          INVIT: d.INVIT ?? d.invit ?? 0,
          'Masala Bonds  ': d.masalaBonds ?? 0,
          Other: d.Other ?? d.other ?? 0
        };
        setCategoryCounts(mappedCounts);
        console.log('Category counts loaded:', mappedCounts);
      } else {
        console.error('Invalid statistics API response structure:', result);
        setCategoryCounts({});
      }
    } catch (error) {
      console.error('Error fetching category statistics:', error);
      setCategoryCounts({});
    }
  };

  // Fetch ISINs by category from the new API
  const fetchIsinsByCategory = async (category, page = 0, size = 100) => {
    try {
      // Set loading state based on whether it's initial load or pagination
      if (page === 0) {
        setLoading(true);
      } else {
        setPaginationLoading(true);
      }

      if (category === 'ALL') {
        // For ALL category, fetch from the master ISIN endpoint
        const response = await apiClient.get(`${baseUrl}/master-isin/all?page=${page}&size=${size}`);
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = response.data;

        let data = [];
        if (result.statusCode === 200 && result.data && result.data.content && Array.isArray(result.data.content)) {
          data = result.data.content;
          setTotalItems(result.data.totalElements || data.length);
        } else if (Array.isArray(result)) {
          data = result;
          setTotalItems(data.length);
        } else {
          console.error('Invalid ALL ISINs API response structure:', result);
          data = [];
          setTotalItems(0);
        }

        setIsinsData(data);
        setFilteredIsins(data);
      } else {
        // For specific categories, use the new category API
        // Map frontend categories to API categories
        let apiCategory;
        if (category === 'slr') {
          apiCategory = 'government'; // SLR maps to government data
        } else if (category === 'sdl') {
          apiCategory = 'stategovernment'; // SDL maps to state government data
        } else if (['nonSlr', 'masalaBonds', 'greenBonds'].includes(category)) {
          // These categories are empty for now
          setIsinsData([]);
          setFilteredIsins([]);
          setTotalItems(0);
          return;
        } else {
          apiCategory = category.toLowerCase();
        }
        
        const response = await apiClient.get(`${baseUrl}/isin-category/category/${apiCategory}?page=${page}&size=${size}`);
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = response.data;

        if (result.statusCode === 200 && result.data) {
          const data = result.data.content || [];
          setIsinsData(data);
          setFilteredIsins(data);
          setTotalItems(result.data.totalElements || data.length);
        } else {
          console.error('Invalid category API response structure:', result);
          setIsinsData([]);
          setFilteredIsins([]);
          setTotalItems(0);
        }
      }
    } catch (error) {
      console.error('Error fetching ISINs by category:', error);
      message.error(`Failed to fetch ISINs for category: ${categories[category] || category}`);
      setIsinsData([]);
      setFilteredIsins([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  };

  // Filter ISINs by search term (client-side filtering)
  const filterIsinsBySearch = () => {
    if (!isinSearchQuery.trim()) {
      setFilteredIsins(isinsData);
      return;
    }

    const searchLower = isinSearchQuery.toLowerCase();
    const filtered = isinsData.filter(isin =>
      isin.isin?.toLowerCase().includes(searchLower) ||
      isin.companyIssuerName?.toLowerCase().includes(searchLower) ||
      isin.industry?.toLowerCase().includes(searchLower) ||
      isin.typeOfIssuerBasedOnOwnership?.toLowerCase().includes(searchLower)
    );

    setFilteredIsins(filtered);
  };

  // Fetch ISINs with advanced filters
  const fetchIsinsWithFilters = async (filters) => {
    try {
      setFilterLoading(true);
      setLoading(true);

      // Use the filter API
      let apiUrl = `${baseUrl}/master-isin/filter`;
      let requestBody = { ...filters };

      const response = await apiClient.post(apiUrl, requestBody);

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = response.data;

      if (result.statusCode === 200 && result.data) {
        const data = result.data.content || [];
        setIsinsData(data);
        setFilteredIsins(data);
        setTotalItems(result.data.totalElements || data.length);
        setCurrentPage(result.data.pageNumber + 1); // Convert to 1-based
        setPageSize(result.data.pageSize);
      } else {
        console.error('Invalid filter API response structure:', result);
        setIsinsData([]);
        setFilteredIsins([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching ISINs with filters:', error);
      message.error('Failed to fetch ISINs with filters');
      setIsinsData([]);
      setFilteredIsins([]);
      setTotalItems(0);
    } finally {
      setFilterLoading(false);
      setLoading(false);
    }
  };

  // Handle filter application with specific page
  const handleFilterApplyWithPage = async (page = 0, size = pageSize) => {
    try {
      // Check if any filter is selected
      if (
        !redemptionFilter
        && !couponRateFilter
        && !(showRatingFilter && ratingsFilter)
      ) {
        message.warning('Please select at least one filter before applying');
        return;
      }

      setFilterLoading(true);
      setLoading(true);

      // Prepare filter payload
      const filterPayload = {
        redemption: redemptionFilter,
        couponRate: couponRateFilter,
      };
      if (showRatingFilter && ratingsFilter) filterPayload.ratings = ratingsFilter;

      const trimmedSearch = isinSearchQuery.trim();
      if (trimmedSearch.length >= MIN_ISIN_SEARCH_QUERY_LENGTH) {
        filterPayload.searchTerm = trimmedSearch;
      }

      // Remove null/undefined values
      Object.keys(filterPayload).forEach(key => {
        if (filterPayload[key] === null || filterPayload[key] === undefined) {
          delete filterPayload[key];
        }
      });

      console.log('Applying filters with page:', page, 'size:', size, 'payload:', filterPayload, 'category:', selectedCategory);

      // Build API URL with category parameter
      let apiUrl = `${baseUrl}/master-isin/filter?page=${page}&size=${size}`;
      if (selectedCategory && selectedCategory !== 'ALL') {
        apiUrl += `&category=${mapCategoryForFilterApi(selectedCategory)}`;
      }

      // Call the filter API with specific page and category
      const response = await apiClient.post(apiUrl, filterPayload);

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = response.data;

      if (result.statusCode === 200 && result.data) {
        const data = result.data.content || [];
        setIsinsData(data);
        setFilteredIsins(data);
        setTotalItems(result.data.totalElements || data.length);
        setCurrentPage(page + 1); // Convert to 1-based for UI
        setPageSize(result.data.pageSize);

        // Only show success message for first page
        if (page === 0) {
          message.success(`Filters applied successfully. Found ${result.data.totalElements} total results.`);
        }
      } else {
        console.error('Invalid filter API response structure:', result);
        setIsinsData([]);
        setFilteredIsins([]);
        setTotalItems(0);
        message.info('No results found for the selected filters');
      }
    } catch (error) {
      console.error('Error applying filters:', error);
      message.error('Failed to apply filters. Please try again.');
      setIsinsData([]);
      setFilteredIsins([]);
      setTotalItems(0);
    } finally {
      setFilterLoading(false);
      setLoading(false);
    }
  };

  // Handle filter application (for initial apply)
  const handleFilterApply = async () => {
    // Update URL params with filter values
    updateURLParams({ 
      redemption: redemptionFilter, 
      couponRate: couponRateFilter, 
      ratings: ratingsFilter, 
      page: 1,
      filtersOpen: 'true'
    });
    await handleFilterApplyWithPage(0, pageSize);
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return redemptionFilter
      || couponRateFilter
      || (showRatingFilter && ratingsFilter);
  };

  const hasActiveSearchQuery = () => (
    isinSearchQuery.trim().length >= MIN_ISIN_SEARCH_QUERY_LENGTH
  );

  // Handle filter clear
  const handleFilterClear = () => {
    setRedemptionFilter(null);
    setCouponRateFilter(null);
    setRatingsFilter(null);
    // Update URL params to remove filter values
    updateURLParams({ 
      redemption: null, 
      couponRate: null, 
      ratings: null, 
      filtersOpen: null
    });
    // Reset to original data
    fetchIsinsByCategory(selectedCategory, 0, pageSize);
    message.info('All filters cleared');
  };

  // Handle filter application
  const handleApplyFilters = (filters) => {
    setCurrentFilters(filters);
    fetchIsinsWithFilters(filters);
    setFilterSidebarVisible(false);
  };

  // Handle filter sidebar toggle
  const handleFilterToggle = () => {
    setFilterSidebarVisible(!filterSidebarVisible);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setIsinSearchQuery(''); // Clear search when changing category
    setCurrentFilters({}); // Clear filters when changing category
    // Clear filters when changing category
    setRedemptionFilter(null);
    setCouponRateFilter(null);
    setRatingsFilter(null);
    // Update URL params
    updateURLParams({ 
      category, 
      page: 1, 
      size: pageSize,
      search: '',
      redemption: null,
      couponRate: null,
      ratings: null,
    });
    // Reset to original category data
    fetchIsinsByCategory(category, 0, pageSize);
  };

  // Handle main category toggle
  const handleMainCategoryToggle = (categoryKey) => {
    // Set the main category as highlighted
    setHighlightedMainCategory(categoryKey);
    
    setExpandedCategories(prev => {
      const newState = {};
      // Close all other categories and toggle the clicked one
      categoryHierarchy.forEach(cat => {
        if (cat.key === categoryKey) {
          newState[cat.key] = !prev[cat.key];
          
          // If opening a main category, automatically select its first subcategory
          if (newState[cat.key] && cat.subcategories && cat.subcategories.length > 0) {
            const firstSubcategory = cat.subcategories[0];
            // Use setTimeout to ensure state updates are processed first
            setTimeout(() => {
              handleCategoryChange(firstSubcategory.key);
            }, 0);
          }
          // Note: Removed the else if condition that was switching to "ALL" when closing
          // Now when manually collapsing, the selected subcategory remains selected
        } else {
          newState[cat.key] = false; // Close all others
        }
      });
      return newState;
    });
  };

  // Handle subcategory click
  const handleSubcategoryClick = (subcategoryKey) => {
    // Keep the main category open when selecting subcategories
    // Don't close expanded categories
    handleCategoryChange(subcategoryKey);
  };

  // Get the main category for a given subcategory
  const getMainCategoryForSubcategory = (subcategoryKey) => {
    if (subcategoryKey === 'ALL') return 'ALL';
    for (const mainCategory of categoryHierarchy) {
      if (mainCategory.key === subcategoryKey) {
        return mainCategory.key;
      }
      if (mainCategory.subcategories?.some((subcategory) => subcategory.key === subcategoryKey)) {
        return mainCategory.key;
      }
    }
    return null;
  };

  // Check if main category should be highlighted
  const isMainCategoryHighlighted = (mainCategoryKey) => {
    // Highlight if it's the highlighted main category OR if "ALL" is selected OR if a subcategory of this main category is selected
    return highlightedMainCategory === mainCategoryKey || 
           (selectedCategory === 'ALL' && mainCategoryKey === 'ALL') ||
           (selectedCategory !== 'ALL' && getMainCategoryForSubcategory(selectedCategory) === mainCategoryKey);
  };

  // Breadcrumb path for current category (same pattern as issuer)
  const getBreadcrumbPath = () => {
    const path = [{ label: 'Instruments', key: null }];
    if (selectedCategory === 'ALL') return path;
    const findIn = (cats, parents) => {
      for (const cat of cats) {
        if (cat.key === selectedCategory) return [...parents, { label: cat.label, key: cat.key }];
        if (cat.subcategories) {
          const found = findIn(cat.subcategories, [...parents, { label: cat.label, key: cat.key }]);
          if (found) return found;
        }
      }
      return null;
    };
    const found = findIn(categoryHierarchy, []);
    if (found) return [...path, ...found];
    return path;
  };

  // Selected category display label for results heading
  const getSelectedCategoryLabel = () => {
    if (selectedCategory === 'ALL') return 'All Instruments';
    const path = getBreadcrumbPath();
    return path.length > 1 ? path.slice(1).map(p => p.label).join(' › ') : 'Instruments';
  };


  // Handle page change
  const handlePageChange = (page, size) => {
    const pageSizeChanged = size !== pageSize;
    const apiPage = pageSizeChanged ? 0 : page - 1;
    const nextUiPage = pageSizeChanged ? 1 : page;

    setCurrentPage(nextUiPage);
    setPageSize(size);
    updateURLParams({ page: nextUiPage, size });

    if (hasActiveFilters()) {
      handleFilterApplyWithPage(apiPage, size);
    } else if (hasActiveSearchQuery()) {
      fetchIsinsBySearchQuery(isinSearchQuery.trim(), apiPage, size);
    } else {
      fetchIsinsByCategory(selectedCategory, apiPage, size);
    }
  };

  // Get paginated results
  const getPaginatedResults = () => {
    // Since we're now fetching paginated data from API, just return the current data
    return filteredIsins;
  };

  const clearIsinSearchTimer = useCallback(() => {
    if (isinSearchTimerRef.current !== null) {
      clearTimeout(isinSearchTimerRef.current);
      isinSearchTimerRef.current = null;
    }
  }, []);

  const fetchIsinsBySearchQuery = useCallback(async (searchQuery, page = 0, size = pageSize) => {
    try {
      setFilterLoading(true);
      setLoading(true);
      if (page > 0) {
        setPaginationLoading(true);
      }

      const searchFilters = {
        searchTerm: searchQuery.trim(),
      };

      let apiUrl = `${baseUrl}/master-isin/filter?page=${page}&size=${size}`;
      if (selectedCategory !== 'ALL') {
        apiUrl += `&category=${mapCategoryForFilterApi(selectedCategory)}`;
      }

      const response = await apiClient.post(apiUrl, searchFilters);

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = response.data;

      if (result.statusCode === 200 && result.data) {
        const data = result.data.content || [];
        setIsinsData(data);
        setFilteredIsins(data);
        setTotalItems(result.data.totalElements || data.length);
        setCurrentPage(page + 1);
        setPageSize(result.data.pageSize || size);
      } else {
        console.error('Invalid search API response structure:', result);
        setIsinsData([]);
        setFilteredIsins([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error searching ISINs:', error);
      message.error('Failed to search ISINs');
      setIsinsData([]);
      setFilteredIsins([]);
      setTotalItems(0);
    } finally {
      setFilterLoading(false);
      setLoading(false);
      setPaginationLoading(false);
    }
  }, [baseUrl, pageSize, selectedCategory]);

  const handleIsinSearchInputChange = useCallback((inputValue) => {
    setIsinSearchQuery(inputValue);
    setCurrentPage(1);
    clearIsinSearchTimer();

    const normalizedQuery = inputValue.trim();

    if (normalizedQuery.length === 0) {
      updateURLParams({ search: null, page: 1 });
      fetchIsinsByCategory(selectedCategory, 0, pageSize);
      return;
    }

    isinSearchTimerRef.current = setTimeout(() => {
      isinSearchTimerRef.current = null;
      if (normalizedQuery.length >= MIN_ISIN_SEARCH_QUERY_LENGTH) {
        updateURLParams({ search: normalizedQuery, page: 1 });
        fetchIsinsBySearchQuery(normalizedQuery);
      }
    }, ISIN_SEARCH_DELAY_MS);
  }, [clearIsinSearchTimer, fetchIsinsBySearchQuery, pageSize, selectedCategory]);

  useEffect(() => () => {
    clearIsinSearchTimer();
  }, [clearIsinSearchTimer]);

  // Fetch data when category changes (but skip on initial mount to avoid conflict with URL restoration)
  useEffect(() => {
    if (isInitialMount.current) {
      // Skip on first mount - let the main initialization useEffect handle it
      return;
    }
    
    setCurrentPage(1); // Reset to page 1 when category changes
    fetchIsinsByCategory(selectedCategory, 0, pageSize);
  }, [selectedCategory]);

  // Auto-expand filters when any filter is applied
  useEffect(() => {
    if (hasActiveFilters() && filtersCollapsed) {
      setFiltersCollapsed(false);
    }
  }, [redemptionFilter, couponRateFilter, ratingsFilter, isinSearchQuery]);

  // Fetch initial data and statistics on component mount - with URL param restoration
  useEffect(() => {
    // Get params from URL or use defaults
    const urlCategory = searchParams.get('category') || 'ALL';
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlSize = parseInt(searchParams.get('size')) || 12;
    const urlSearch = searchParams.get('search') || '';
    const urlRedemption = searchParams.get('redemption');
    const urlCouponRate = searchParams.get('couponRate');
    const urlRatings = searchParams.get('ratings');
    
    // Update state if URL params are different
    if (urlCategory !== selectedCategory) {
      setSelectedCategory(urlCategory);
    }
    if (urlPage !== currentPage) {
      setCurrentPage(urlPage);
    }
    if (urlSize !== pageSize) {
      setPageSize(urlSize);
    }
    if (urlSearch !== isinSearchQuery) {
      setIsinSearchQuery(urlSearch);
    }
    
    // Restore filter states from URL (always sync on mount / return navigation)
    setRedemptionFilter(urlRedemption || null);
    setCouponRateFilter(urlCouponRate || null);
    setRatingsFilter(urlRatings || null);
    
    // Highlight active main bucket from URL — keep category dropdowns closed on load
    if (urlCategory === 'ALL') {
      setHighlightedMainCategory(null);
    } else {
      const mainCat = getMainCategoryForSubcategory(urlCategory);
      if (mainCat) setHighlightedMainCategory(mainCat);
    }
    setExpandedCategories({});
    
    // Check if filters are active and should auto-apply
    const hasFilters = urlRedemption || urlCouponRate || urlRatings;
    if (hasFilters) {
      // Apply filters with the URL params
      handleFilterApplyWithPage(urlPage - 1, urlSize);
    } else if (urlSearch && urlSearch.length >= MIN_ISIN_SEARCH_QUERY_LENGTH) {
      fetchIsinsBySearchQuery(urlSearch, urlPage - 1, urlSize);
    } else {
      // Normal category fetch
      fetchIsinsByCategory(urlCategory, urlPage - 1, urlSize);
    }
    
    // Fetch statistics
    fetchCategoryStatistics();
    
    // Mark initial mount as complete
    isInitialMount.current = false;
  }, []); // Only run on mount

  // Helper function to get ISIN logo/icon - Professional approach
  const getIsinLogo = (isin, industry) => {
    // Industry-based icons - Professional symbols
    if (industry === 'Finance') return '🏦';
    if (industry === 'Fertilizers & Agrochemicals') return '🏭';
    if (industry === 'Utilities') return '⚡';
    if (industry === 'Technology') return '💻';
    if (industry === 'Healthcare') return '🏥';
    if (industry === 'Real Estate') return '🏠';
    if (industry === 'Energy') return '⚡';
    if (industry === 'Consumer Goods') return '🛒';

    // Default to first 2 characters of ISIN for consistency
    return isin.substring(0, 2);
  };

  // Helper function to get ISIN short name
  const getIsinShortName = (isin) => {
    return isin;
  };

  // Helper function to get truncated text for details
  const getTruncatedText = (text, maxLength = 20) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Helper function to handle array values (like stockExchange)
  const formatArrayValue = (value) => {
    console.log('formatArrayValue input:', value, 'type:', typeof value, 'isArray:', Array.isArray(value));

    if (!value) return 'N/A';
    if (Array.isArray(value)) {
      const result = value.join(', ');
      console.log('formatArrayValue array result:', result);
      return result;
    }
    // Handle string that might be JSON array
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          const result = parsed.join(', ');
          console.log('formatArrayValue JSON parse result:', result);
          return result;
        }
      } catch (e) {
        console.log('formatArrayValue JSON parse failed:', e);
        // If parsing fails, return as is
      }
    }
    // Handle string that contains array-like content
    if (typeof value === 'string' && value.includes('[') && value.includes(']')) {
      try {
        // Try to extract array content
        const match = value.match(/\[(.*)\]/);
        if (match) {
          const content = match[1];
          // Split by comma and clean up quotes
          const items = content.split(',').map(item => item.trim().replace(/"/g, ''));
          const result = items.join(', ');
          console.log('formatArrayValue regex result:', result);
          return result;
        }
      } catch (e) {
        console.log('formatArrayValue regex failed:', e);
        // If parsing fails, return as is
      }
    }
    console.log('formatArrayValue default return:', value);
    return value;
  };

  // Helper function to get category color - Professional financial colors
  const getCategoryColor = (industry) => {
    const colors = {
      'Finance': 'blue',
      'Fertilizers & Agrochemicals': 'green',
      'Utilities': 'orange',
      'Technology': 'purple',
      'Healthcare': 'cyan',
      'Real Estate': 'geekblue',
      'Energy': 'orange',
      'Consumer Goods': 'lime'
    };
    return colors[industry] || 'default';
  };

  // Helper function to get category icon - Professional icons
  const getCategoryIcon = (industry) => {
    const icons = {
      'Finance': <BankOutlined />,
      'Fertilizers & Agrochemicals': <HomeOutlined />,
      'Utilities': <RocketOutlined />,
      'Technology': <CarOutlined />,
      'Healthcare': <SafetyOutlined />,
      'Real Estate': <HomeOutlined />,
      'Energy': <RocketOutlined />,
      'Consumer Goods': <ShopOutlined />
    };
    return icons[industry] || <ShopOutlined />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to get rating color based on credit rating
  const getRatingColor = (rating) => {
    if (!rating || rating === 'N/A' || rating === '-') return 'default';

    const ratingUpper = rating.toUpperCase();

    // AAA ratings - highest quality (green)
    if (ratingUpper.includes('AAA')) return 'success';

    // AA ratings - high quality (blue)
    if (ratingUpper.includes('AA')) return 'processing';

    // A ratings - upper medium grade (cyan)
    if (ratingUpper.includes('A')) return 'cyan';

    // BBB ratings - medium grade (orange)
    if (ratingUpper.includes('BBB')) return 'warning';

    // BB and below - speculative/junk (red)
    if (ratingUpper.includes('BB') || ratingUpper.includes('B') || ratingUpper.includes('C') || ratingUpper.includes('D')) return 'error';

    // Default for unknown ratings
    return 'default';
  };

  const handleViewDetails = (isin) => {
    // Use corporateIdentityNumber as the identifier for navigation
    const isinId = isin.isin;
    // Navigate to dashboard route if we're in dashboard context, otherwise to landing page
    const isInDashboard = window.location.pathname.includes('/d/');
    if (isInDashboard) {
      navigate(`/d/isin-details/${isinId}`);
    } else {
      navigate(`/isin-details/${isinId}`);
    }
  };

  const content = (
      <div className={`isin-page ${isDark ? 'dark' : ''}`}>
        <div className="container">
          <header className="instruments-toolbar">
            <div className="instruments-toolbar__primary">
              <div className="instruments-view-tabs" role="tablist" aria-label="Instruments views">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeView === 'browse'}
                  className={`instruments-view-tab${activeView === 'browse' ? ' instruments-view-tab--active' : ''}`}
                  onClick={() => setActiveView('browse')}
                >
                  Browse ISINs
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeView === 'watchlist'}
                  className={`instruments-view-tab${activeView === 'watchlist' ? ' instruments-view-tab--active' : ''}`}
                  onClick={() => setActiveView('watchlist')}
                >
                  <StarOutlined />
                  Watchlist
                </button>
              </div>
              {activeView === 'browse' ? (
                <p className="instruments-toolbar__meta">
                  <strong>{getSelectedCategoryLabel()}</strong>
                  <span>
                    {` · ${loading || paginationLoading ? 'Loading…' : `${totalItems.toLocaleString()} ISIN${totalItems === 1 ? '' : 's'}`}`}
                  </span>
                </p>
              ) : (
                <p className="instruments-toolbar__meta">Bonds you are tracking</p>
              )}
            </div>
            {activeView === 'browse' && (
              <div className="instruments-toolbar__actions">
                <div className="search-input-container">
                  <Input
                    className="isin-search-input"
                    placeholder="Search ISIN or issuer..."
                    prefix={<SearchOutlined className="search-input-icon" />}
                    suffix={filterLoading ? <Spin size="small" className="search-spinner" /> : null}
                    value={isinSearchQuery}
                    onChange={(e) => handleIsinSearchInputChange(e.target.value)}
                    allowClear
                  />
                </div>
                <Button
                  type="default"
                  icon={<FilterOutlined />}
                  onClick={() => {
                    const newCollapsedState = !filtersCollapsed;
                    setFiltersCollapsed(newCollapsedState);
                    updateURLParams({ filtersOpen: newCollapsedState ? null : 'true' });
                  }}
                  className={`advanced-filter-btn ${filtersCollapsed ? 'collapsed' : 'expanded'} ${hasActiveFilters() ? 'has-active-filters' : ''}`}
                >
                  Filters
                  {getActiveFiltersCount() > 0 && (
                    <span className="advanced-filter-count">{getActiveFiltersCount()}</span>
                  )}
                </Button>
              </div>
            )}
          </header>

        {activeView === 'watchlist' ? (
          <WatchlistPanel embedded />
        ) : (
        <>

        {/* Category Tabs - Commented out in favor of category buttons */}
        {/* <div className="category-tabs-section">
          <Tabs
            activeKey={selectedCategory}
            onChange={handleCategoryChange}
            className="category-tabs"
            size="large"
            type="card"
          >
            {Object.entries(categories).map(([key, label]) => {
              // Get count for each category
              let count = 0;
              if (key === 'ALL') {
                count = categoryCounts.totalIsins || 0;
              } else {
                count = categoryCounts[key] || 0;
              }

              return (
                <TabPane
                  tab={
                    <span>
                      {label}
                      <span className="category-count"> ({count})</span>
                    </span>
                  }
                  key={key}
                />
              );
            })}
          </Tabs>
        </div> */}

        {/* Search and Filter Controls - Below Tabs, Right Side */}
       

        <div className="filter-section">
          <Card
            className={`filter-card ${filtersCollapsed ? 'collapsed' : 'expanded'} ${hasActiveFilters() ? 'has-active-filters' : ''}`}
          >
            {!filtersCollapsed && (
              <div className="instruments-filter-panel">
                <div
                  className="instruments-filter-grid"
                  style={{ '--filter-columns': 2 + (showRatingFilter ? 1 : 0) }}
                >
                  <div className="instruments-filter-field">
                    <label className="instruments-filter-label" htmlFor="filter-redemption">
                      Redemption
                    </label>
                    <Select
                      id="filter-redemption"
                      placeholder="Any period"
                      value={redemptionFilter}
                      onChange={setRedemptionFilter}
                      style={{ width: '100%' }}
                      options={redemptionOptions}
                      allowClear
                      size="middle"
                      dropdownStyle={{ zIndex: 10100 }}
                      className="filter-select"
                    />
                  </div>

                  <div className="instruments-filter-field">
                    <label className="instruments-filter-label" htmlFor="filter-coupon">
                      Coupon
                    </label>
                    <Select
                      id="filter-coupon"
                      placeholder="Any rate"
                      value={couponRateFilter}
                      onChange={setCouponRateFilter}
                      style={{ width: '100%' }}
                      options={couponRateOptions}
                      allowClear
                      size="middle"
                      dropdownStyle={{ zIndex: 10100 }}
                      className="filter-select"
                    />
                  </div>

                  {showRatingFilter && (
                    <div className="instruments-filter-field">
                      <label className="instruments-filter-label" htmlFor="filter-rating">
                        Rating
                      </label>
                      <Select
                        id="filter-rating"
                        placeholder="Any rating"
                        value={ratingsFilter}
                        onChange={setRatingsFilter}
                        style={{ width: '100%' }}
                        options={ratingsOptions}
                        allowClear
                        size="middle"
                        dropdownStyle={{ zIndex: 10100 }}
                        className="filter-select"
                      />
                    </div>
                  )}
                </div>

                <div className="filter-actions">
                  <Button
                    onClick={handleFilterClear}
                    disabled={!hasActiveFilters()}
                    size="small"
                    className="filter-clear-btn"
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleFilterApply}
                    loading={filterLoading}
                    disabled={!hasActiveFilters()}
                    size="small"
                    className="filter-apply-btn"
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Categories — horizontal menu + dropdown (same as issuer categorization) */}
        <section ref={categoriesSectionRef} className="categories-section" aria-label="Browse by category">
          <div className="horizontal-menu-bar">
            {categoryHierarchy.map((category) => (
              <div
                key={category.key}
                className={`menu-item ${isMainCategoryHighlighted(category.key) ? 'active' : ''} ${expandedCategories[category.key] ? 'expanded' : ''}`}
                onClick={() => {
                  if (category.subcategories) {
                    setExpandedCategories(prev => {
                      const newState = {};
                      categoryHierarchy.forEach(cat => {
                        newState[cat.key] = cat.key === category.key ? !prev[cat.key] : false;
                      });
                      return newState;
                    });
                  } else {
                    setExpandedCategories({});
                    setHighlightedMainCategory(null);
                    handleCategoryChange(category.key);
                  }
                }}
              >
                <div className="menu-item-icon">{category.icon}</div>
                <div className="menu-item-content">
                  <div className="menu-item-label">
                    {category.label}
                    {categoryCounts[category.key] !== undefined && (
                      <span style={{ marginLeft: '6px', opacity: 0.8, fontWeight: 'normal' }}>
                        ({categoryCounts[category.key] || 0})
                      </span>
                    )}
                  </div>
                </div>
                {category.subcategories && (
                  <div className="menu-item-dropdown-arrow">▼</div>
                )}
                {category.subcategories && expandedCategories[category.key] && (
                  <div
                    className="category-dropdown-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {category.subcategories.map((subcategory) => (
                      <div
                        key={subcategory.key}
                        className={`dropdown-item ${selectedCategory === subcategory.key ? 'active' : ''}`}
                        onClick={() => handleSubcategoryClick(subcategory.key)}
                      >
                        <div className="dropdown-item-icon">{subcategory.icon}</div>
                        <span className="dropdown-item-label">
                          {subcategory.label}
                          {categoryCounts[subcategory.key] !== undefined && (
                            <span style={{ marginLeft: '6px', opacity: 0.8 }}>
                              ({categoryCounts[subcategory.key] || 0})
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ISIN Grid */}
        <div className="isin-grid">
          {loading || paginationLoading ? (
            // Loading skeleton — same structure as ISINCard
            <Row gutter={[24, 24]}>
              {Array.from({ length: 8 }).map((_, index) => (
                <Col xs={24} sm={12} lg={8} key={index}>
                  <Card className="bond-card isin-card isin-card--premium loading" hoverable style={{ borderRadius: '20px', backgroundColor: '#f1f0e8' }}>
                    <div className="bond-card-inner isin-card-premium-inner">
                      <div className="isin-card-premium__head">
                        <div className="isin-card-premium__head-left">
                          <div className="loading-placeholder" style={{ width: 140, height: 10, backgroundColor: '#e2e8f0', borderRadius: 4 }} />
                          <div className="loading-placeholder" style={{ width: '90%', height: 22, backgroundColor: '#e2e8f0', borderRadius: 4 }} />
                          <div className="loading-placeholder" style={{ width: '65%', height: 14, backgroundColor: '#e2e8f0', borderRadius: 4 }} />
                        </div>
                        <div className="isin-card-premium__head-right">
                          <div className="loading-placeholder" style={{ width: 130, height: 44, backgroundColor: '#e2e8f0', borderRadius: 999 }} />
                        </div>
                      </div>
                      <div className="loading-placeholder" style={{ width: '100%', height: 72, backgroundColor: '#e2e8f0', borderRadius: 12 }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
                        <div>
                          <div className="loading-placeholder" style={{ width: 120, height: 10, backgroundColor: '#e2e8f0', borderRadius: 4, marginBottom: 8 }} />
                          <div className="loading-placeholder" style={{ width: 88, height: 28, backgroundColor: '#e2e8f0', borderRadius: 4 }} />
                        </div>
                        <div className="loading-placeholder" style={{ width: 130, height: 40, backgroundColor: '#e2e8f0', borderRadius: 999 }} />
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : filteredIsins.length > 0 ? (
            <Row gutter={[16, 16]}>
              {getPaginatedResults().map((isin, index) => (
                <Col xs={24} sm={12} lg={8} xl={8} key={index}>
                  <ISINCard
                    isin={isin}
                    index={index}
                    onViewDetails={handleViewDetails}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <Title level={3}>No ISINs found</Title>
              <Paragraph>
                {isinSearchQuery.trim()
                  ? `No ISINs found matching "${isinSearchQuery}" in ${categories[selectedCategory]}.`
                  : `No ISINs found in ${categories[selectedCategory]}.`
                }
              </Paragraph>
              {isinSearchQuery.trim() && (
                <Button
                  type="primary"
                  onClick={() => handleIsinSearchInputChange('')}
                  className="clear-search-btn"
                >
                  Clear Search
                </Button>
              )}
            </div>
          )}
        </div>
        {totalItems > 0 && (
          <div className="pagination-container">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalItems}
              onChange={handlePageChange}
              showSizeChanger
              onShowSizeChange={handlePageChange}
              showTotal={(total) => {
                const start = ((currentPage - 1) * pageSize) + 1;
                const end = Math.min(currentPage * pageSize, total);
                return `Showing ${start}-${end} of ${total} ISINs`;
              }}
              pageSizeOptions={['12', '24', '48', '96']}
            />
          </div>
        )}
        </>
        )}
        </div>
    </div>
  );

  return skipLayout ? (
    <>
      {content}
      <ISINFilterSidebar
        visible={filterSidebarVisible}
        onClose={() => setFilterSidebarVisible(false)}
        onApplyFilters={handleApplyFilters}
        loading={filterLoading}
        currentFilters={currentFilters}
      />
    </>
  ) : (
    <LandingLayout>
      {content}
      <ISINFilterSidebar
        visible={filterSidebarVisible}
        onClose={() => setFilterSidebarVisible(false)}
        onApplyFilters={handleApplyFilters}
        loading={filterLoading}
        currentFilters={currentFilters}
      />
    </LandingLayout>
  );
};

export default Instruments;
